import React, { useState, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Box,
  Typography,
  Checkbox,
  Tooltip,
  Button,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid';
import { Badge } from '../lib/format';
import { Button as OdButton } from "./ui/Button";

interface ColumnNode {
  field: string;
  headerName: string;
  isVisible: boolean;
  isExpanded?: boolean;
  isExpandableField?: boolean;
  children: ColumnNode[];
  level: number;
  colDef: GridColDef;
}

interface ColumnTreeViewProps {
  columns: GridColDef[];
  columnVisibility: { [key: string]: boolean };
  onColumnVisibilityChange: (newModel: { [key: string]: boolean }) => void;
}

export const ColumnTreeView: React.FC<ColumnTreeViewProps> = React.memo(({
  columns,
  columnVisibility,
  onColumnVisibilityChange,
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsOpen(true);
  };

  const handleClose = (event?: any, reason?: string) => {
    if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
      setIsOpen(false);
    }
  };

  const handleManualClose = () => {
    setIsOpen(false);
  };

  const open = isOpen;
  const id = open ? 'column-tree-popover' : undefined;

  // Build tree structure from flat columns array
  const columnTree = useMemo(() => {
    const tree: ColumnNode[] = [];
    const nodeMap = new Map<string, ColumnNode>();

    // Separate columns into root and expanded
    const rootColumns: GridColDef[] = [];
    const expandedColumns = new Map<string, GridColDef[]>(); // parentPath -> columns

    columns.forEach(col => {
      if (col.field.startsWith('__expanded_')) {
        // Extract parent path from __expanded_path_subField
        const match = col.field.match(/^__expanded_(.+?)_([^_]+)$/);
        if (match) {
          const parentPath = match[1]; // e.g., "job" or "job.availability"
          if (!expandedColumns.has(parentPath)) {
            expandedColumns.set(parentPath, []);
          }
          expandedColumns.get(parentPath)!.push(col);
        } else {
          // Fallback: treat as root if parsing fails
          rootColumns.push(col);
        }
      } else {
        rootColumns.push(col);
      }
    });

    // Create nodes for root columns
    rootColumns.forEach(col => {
      const isVisible = columnVisibility[col.field] !== false;
      const hasChildren = expandedColumns.has(col.field.replace(/Id$/, ''));
      
      const node: ColumnNode = {
        field: col.field,
        headerName: col.headerName || col.field,
        isVisible,
        isExpandableField: hasChildren,
        children: [],
        level: 0,
        colDef: col,
      };

      tree.push(node);
      nodeMap.set(col.field, node);
    });

    // Process expanded columns and build hierarchy
    const processedPaths = new Set<string>();
    
    // Sort expanded column paths by depth (shallow first)
    const sortedPaths = Array.from(expandedColumns.keys()).sort((a, b) => {
      const depthA = a.split('.').length;
      const depthB = b.split('.').length;
      return depthA - depthB;
    });

    sortedPaths.forEach(parentPath => {
      const cols = expandedColumns.get(parentPath)!;
      const pathParts = parentPath.split('.');
      const level = pathParts.length;

      // Find parent node
      let parentNode: ColumnNode | undefined;
      
      if (level === 1) {
        // Top-level expansion (e.g., "job")
        // Parent is the FK field (e.g., "jobId")
        const parentFieldName = `${parentPath}Id`;
        parentNode = nodeMap.get(parentFieldName);
      } else {
        // Nested expansion (e.g., "job.availability")
        // Parent is a virtual node for the path
        parentNode = nodeMap.get(parentPath);
      }

      // Create child nodes
      cols.forEach(col => {
        const isVisible = columnVisibility[col.field] !== false;
        const fieldName = col.field.replace(`__expanded_${parentPath}_`, '');
        const hasChildren = expandedColumns.has(`${parentPath}.${fieldName.replace(/Id$/, '')}`);
        
        const childNode: ColumnNode = {
          field: col.field,
          headerName: col.headerName || col.field,
          isVisible,
          isExpandableField: hasChildren,
          children: [],
          level,
          colDef: col,
        };

        if (parentNode) {
          parentNode.children.push(childNode);
        } else {
          // If parent not found, add to root
          tree.push(childNode);
        }
        
        // Register this node for potential nested children
        nodeMap.set(`${parentPath}.${fieldName.replace(/Id$/, '')}`, childNode);
        nodeMap.set(col.field, childNode);
      });

      processedPaths.add(parentPath);
    });

    return tree;
  }, [columns, columnVisibility]);

  // Memoize the visibility state calculator
  const getNodeVisibilityState = useCallback((node: ColumnNode): { checked: boolean; indeterminate: boolean } => {
    if (node.children.length === 0) {
      // Leaf node: just check its own visibility
      return { checked: node.isVisible, indeterminate: false };
    }
    
    // Parent node: check children recursively
    const checkChildren = (n: ColumnNode): { checked: boolean; indeterminate: boolean } => {
      if (n.children.length === 0) {
        return { checked: n.isVisible, indeterminate: false };
      }
      
      const childStates = n.children.map(child => checkChildren(child));
      const allChildrenVisible = childStates.every(state => state.checked && !state.indeterminate);
      const noChildrenVisible = childStates.every(state => !state.checked && !state.indeterminate);
      
      if (allChildrenVisible && n.isVisible) {
        return { checked: true, indeterminate: false };
      } else if (noChildrenVisible && !n.isVisible) {
        return { checked: false, indeterminate: false };
      } else {
        return { checked: false, indeterminate: true };
      }
    };
    
    return checkChildren(node);
  }, []);

  // Keep original column order to prevent jumping when toggling visibility
  const sortedColumnTree = columnTree;

  // Calculate visibility statistics
  const visibilityStats = useMemo(() => {
    let visible = 0;
    let partial = 0;
    let total = 0;

    // Count leaf columns only (non-parent columns)
    columns.forEach(col => {
      if (col.field !== 'actions') { // Exclude actions column
        total++;
        if (columnVisibility[col.field] !== false) {
          visible++;
        }
      }
    });
    
    // Count indeterminate parent nodes
    columnTree.forEach(node => {
      const countIndeterminate = (n: ColumnNode): number => {
        const state = getNodeVisibilityState(n);
        let count = state.indeterminate ? 1 : 0;
        n.children.forEach(child => {
          count += countIndeterminate(child);
        });
        return count;
      };
      partial += countIndeterminate(node);
    });

    return { visible, total, partial };
  }, [columns, columnVisibility, columnTree, getNodeVisibilityState]);

  const handleNodeToggle = (field: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(field)) {
        newSet.delete(field);
      } else {
        newSet.add(field);
      }
      return newSet;
    });
  };

  const handleVisibilityToggle = (node: ColumnNode, event: React.MouseEvent) => {
    // Prevent all event propagation
    event.stopPropagation();
    event.preventDefault();
    
    const newVisibility = { ...columnVisibility };
    const currentState = getNodeVisibilityState(node);
    
    // If indeterminate or unchecked, select all (turn on)
    // If checked, deselect all (turn off)
    const newValue = currentState.indeterminate || !currentState.checked;
    
    // Toggle this column
    newVisibility[node.field] = newValue;
    
    // If this node has children, toggle them all
    const toggleChildren = (n: ColumnNode, value: boolean) => {
      n.children.forEach(child => {
        newVisibility[child.field] = value;
        if (child.children.length > 0) {
          toggleChildren(child, value);
        }
      });
    };
    
    if (node.children.length > 0) {
      toggleChildren(node, newValue);
    }
    
    // Update immediately without RAF
    onColumnVisibilityChange(newVisibility);
  };

  const showAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const newVisibility: { [key: string]: boolean } = {};
    columns.forEach(col => {
      newVisibility[col.field] = true;
    });
    onColumnVisibilityChange(newVisibility);
  };

  const hideAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const newVisibility: { [key: string]: boolean } = {};
    columns.forEach(col => {
      // Keep actions column visible
      if (col.field === 'actions') {
        newVisibility[col.field] = true;
      } else {
        newVisibility[col.field] = false;
      }
    });
    onColumnVisibilityChange(newVisibility);
  };

  const capitalize = (text: string) => {
    return text
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim();
  };

  const renderTreeNode = (node: ColumnNode): React.ReactNode => {
    const hasChildren = node.children.length > 0;
    const isNodeExpanded = expandedNodes.has(node.field);
    const visibilityState = getNodeVisibilityState(node);
    
    // Count visible children recursively
    const countVisibleChildren = (n: ColumnNode): { visible: number; total: number } => {
      if (n.children.length === 0) {
        return { visible: 0, total: 0 };
      }
      
      let visible = 0;
      let total = 0;
      n.children.forEach(child => {
        total++;
        const childState = getNodeVisibilityState(child);
        if (childState.checked || childState.indeterminate) {
          visible++;
        }
        
        // Add nested children
        const nested = countVisibleChildren(child);
        total += nested.total;
        visible += nested.visible;
      });
      
      return { visible, total };
    };
    
    const childCounts = hasChildren ? countVisibleChildren(node) : { visible: 0, total: 0 };

    return (
      <React.Fragment key={node.field}>
        <ListItem
          dense
          onClick={(e) => {
            e.stopPropagation();
            handleVisibilityToggle(node, e);
          }}
          sx={{
            pl: node.level * 2 + 1,
            py: 0.75,
            transition: 'all 0.15s ease-in-out',
            cursor: 'pointer', // Changed from 'default' to indicate clickability
            '&:hover': {
              backgroundColor: visibilityState.indeterminate 
                ? 'color-mix(in srgb, var(--od-warning) 8%, transparent)' 
                : 'color-mix(in srgb, var(--od-muted) 4%, transparent)',
            },
            borderLeft: node.level > 0 
              ? `3px solid ${visibilityState.indeterminate ? 'color-mix(in srgb, var(--od-warning) 36%, transparent)' : 'color-mix(in srgb, var(--od-primary) 20%, transparent)'}` 
              : 'none',
            marginLeft: node.level > 0 ? `${node.level * 12}px` : 0,
            backgroundColor: visibilityState.indeterminate 
              ? 'color-mix(in srgb, var(--od-warning) 4%, transparent)' 
              : 'transparent',
            borderRadius: '6px',
            mb: 0.5,
          }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              aria-label={capitalize(node.headerName)}
              aria-expanded={isNodeExpanded}
              onClick={(e) => {
                e.stopPropagation(); // Prevent ListItem click from firing
                handleNodeToggle(node.field, e);
              }}
              sx={{ 
                mr: 0.5, 
                padding: '4px',
                transition: 'transform 0.2s ease',
                transform: isNodeExpanded ? 'rotate(0deg)' : 'rotate(0deg)',
                '&:hover': {
                  backgroundColor: 'color-mix(in srgb, var(--od-muted) 8%, transparent)',
                },
              }}
            >
              {isNodeExpanded ? (
                <ExpandMore fontSize="small" />
              ) : (
                <ChevronRight fontSize="small" />
              )}
            </IconButton>
          ) : (
            <Box sx={{ width: 28, mr: 0.5 }} />
          )}
          
          <ListItemIcon 
            sx={{ minWidth: 32 }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {visibilityState.indeterminate ? (
              <RemoveCircleOutlineIcon 
                fontSize="small" 
                sx={{ 
                  color: 'var(--od-warning)',
                  transition: 'all 0.2s ease',
                }} 
              />
            ) : visibilityState.checked ? (
              <CheckCircleIcon 
                fontSize="small" 
                sx={{ 
                  color: 'success.main',
                  transition: 'all 0.2s ease',
                }} 
              />
            ) : (
              <RadioButtonUncheckedIcon 
                fontSize="small" 
                sx={{ 
                  color: 'var(--od-muted)',
                  opacity: 0.35,
                  transition: 'all 0.2s ease',
                }} 
              />
            )}
          </ListItemIcon>
          
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span>{capitalize(node.headerName)}</span>
                {hasChildren && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: visibilityState.indeterminate ? 'var(--od-warning)' : 'var(--od-muted)',
                      fontSize: '0.75rem',
                      backgroundColor: visibilityState.indeterminate 
                        ? 'color-mix(in srgb, var(--od-warning) 16%, transparent)' 
                        : 'color-mix(in srgb, var(--od-muted) 6%, transparent)',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontWeight: visibilityState.indeterminate ? 600 : 500,
                      lineHeight: 1.3,
                      border: visibilityState.indeterminate 
                        ? '1px solid color-mix(in srgb, var(--od-warning) 36%, transparent)' 
                        : '1px solid transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {childCounts.visible}/{childCounts.total}
                  </Typography>
                )}
              </Box>
            }
            secondary={node.level === 0 ? node.field : undefined}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: visibilityState.indeterminate 
                ? 500 
                : visibilityState.checked 
                  ? 500 
                  : 400,
              color: visibilityState.indeterminate 
                ? 'var(--od-text)' 
                : visibilityState.checked 
                  ? 'var(--od-text)' 
                  : 'var(--od-muted)',
            }}
            secondaryTypographyProps={{
              fontSize: '0.75rem',
              color: 'var(--od-muted)',
            }}
          />
          
          <Tooltip 
            title={
              visibilityState.indeterminate 
                ? 'Partially visible - click to show all' 
                : visibilityState.checked 
                  ? 'Click to hide' 
                  : 'Click to show'
            }
            arrow
            placement="left"
          >
            <Checkbox
              size="small"
              inputProps={{ 'aria-label': capitalize(node.headerName) }}
              checked={visibilityState.checked}
              indeterminate={visibilityState.indeterminate}
              onChange={(e) => {
                e.stopPropagation();
                handleVisibilityToggle(node, e as any);
              }}
              onClick={(e) => {
                // Only stop propagation to prevent ListItem click
                // Don't preventDefault - let the checkbox work normally
                e.stopPropagation();
              }}
              sx={{ 
                padding: '6px',
                transition: 'all 0.2s ease',
                color: visibilityState.indeterminate ? 'var(--od-warning)' : undefined,
                '&.MuiCheckbox-indeterminate': {
                  color: 'var(--od-warning)',
                },
                '&:hover': {
                  backgroundColor: visibilityState.indeterminate 
                    ? 'color-mix(in srgb, var(--od-warning) 8%, transparent)' 
                    : undefined,
                },
              }}
            />
          </Tooltip>
        </ListItem>
        
        {hasChildren && (
          <Collapse in={isNodeExpanded} timeout={200} unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 0.5 }}>
              {node.children.map(child => renderTreeNode(child))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      <Tooltip title="Show or hide columns">
        <OdButton
          ref={buttonRef}
          onClick={handleClick}
          variant="secondary" size="sm"
        >
          <ViewColumnIcon sx={{ fontSize: 15 }} />
          <span>
            Columns
            {visibilityStats.visible < visibilityStats.total && (
              <span className="ml-1 text-od-muted">
                {visibilityStats.visible}/{visibilityStats.total}
              </span>
            )}
          </span>
        </OdButton>
      </Tooltip>
      
      {createPortal(
        <Popover
        id={id}
        open={open}
        anchorEl={buttonRef.current}
        onClose={handleClose}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        disableEnforceFocus
        disableAutoFocus
        disableScrollLock={false}
        BackdropProps={{
          invisible: true,
          sx: { 
            pointerEvents: 'auto',
            cursor: 'default'
          }
        }}
        PaperProps={{
          elevation: 8,
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
          },
          onMouseDown: (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
          },
          sx: {
            maxHeight: '70vh',
            minWidth: '380px',
            maxWidth: '520px',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px color-mix(in srgb, var(--od-muted) 12%, transparent), 0 2px 8px color-mix(in srgb, var(--od-muted) 8%, transparent)',
          },
        }}
      >
        <Box sx={{ 
          p: 2.5, 
          borderBottom: '1px solid', 
          borderColor: 'var(--od-border)',
          backgroundColor: 'var(--od-surface)',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
            <Box>
              <h2 className="text-od-text">
                Column Visibility
              </h2>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span className="text-od-muted">
                  {visibilityStats.visible} / {visibilityStats.total} shown
                </span>
                {visibilityStats.partial > 0 && (
                  <Tooltip title={`${visibilityStats.partial} parent${visibilityStats.partial > 1 ? 's' : ''} with partial selection`} arrow>
                    <span className="cursor-help">
                      <Badge
                        tone="warning"
                        value={`~${visibilityStats.partial}`}
                      />
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
            <Tooltip title="Close" arrow>
              <IconButton
                size="small"
                onClick={handleManualClose}
                sx={{
                  padding: '4px',
                  '&:hover': {
                    backgroundColor: 'color-mix(in srgb, var(--od-muted) 8%, transparent)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<VisibilityIcon fontSize="small" />}
              onClick={(e) => showAll(e)}
              variant="outlined"
              sx={{ 
                flex: 1, 
                textTransform: 'none', 
                fontSize: '0.8rem',
                fontWeight: 500,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'color-mix(in srgb, var(--od-primary) 10%, transparent)',
                  borderColor: 'var(--od-primary)',
                },
              }}
            >
              Show All
            </Button>
            <Button
              size="small"
              startIcon={<VisibilityOffIcon fontSize="small" />}
              onClick={(e) => hideAll(e)}
              variant="outlined"
              sx={{ 
                flex: 1, 
                textTransform: 'none', 
                fontSize: '0.8rem',
                fontWeight: 500,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'color-mix(in srgb, var(--od-muted) 4%, transparent)',
                },
              }}
            >
              Hide All
            </Button>
          </Box>
        </Box>
        
        <List sx={{ 
          width: '100%', 
          bgcolor: 'var(--od-surface)', 
          maxHeight: 'calc(70vh - 140px)', 
          overflow: 'auto',
          p: 1.5,
          pt: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'color-mix(in srgb, var(--od-muted) 5%, transparent)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'color-mix(in srgb, var(--od-muted) 20%, transparent)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'color-mix(in srgb, var(--od-muted) 30%, transparent)',
            },
          },
        }}>
          {sortedColumnTree.map((node) => renderTreeNode(node))}
        </List>
      </Popover>,
      document.body
      )}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: skip render if columns and visibility are identical
  // Include callback comparison to ensure proper updates when needed
  // The popover state should remain stable, but we need updates when visibility changes
  return (
    prevProps.columns === nextProps.columns &&
    prevProps.columnVisibility === nextProps.columnVisibility &&
    prevProps.onColumnVisibilityChange === nextProps.onColumnVisibilityChange
  );
});

